import {Component, OnDestroy, ViewEncapsulation} from '@angular/core';
import {NavigateService, ShortcutAction, LangService, LoaderService} from 'ngx-satoris';
import {ApiService} from '../../../shared/services/api.service';
import {PlacesService} from 'src/app/shared/services/places.service';
import {Document, UserPermission} from '../../../shared/models/user';
import {CartService} from '../../../shared/services/cart.service';
import {PassportType} from '../../../shared/models/request';
import {RequestService} from '../../../shared/services/request.service';
import {InactivityService} from '../../../shared/services/inactivity.service';
import {Direction, VisaFN} from '../../../shared/models/forms';
import {ScanDocumentService} from 'src/app/shared/services/scan-document.service';
import {ActivatedRoute} from '@angular/router';
import {getVisaTypes, isCanHaveOnArrival, setCurrentCountryCat} from 'src/app/shared/models/default-filter-strategy';
import {documentTypeIsVisaMultiple} from 'src/app/shared/models/document-type-helpers';

@Component({
  selector: 'app-4.visa-type',
  templateUrl: './visa-type.component.html',
  styleUrl: './visa-type.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class VisaTypeComponent implements OnDestroy {

  selected: Document = this.cart.currentRequest?.visaType || undefined;
  visaTypes: Document[];
  keepScanned = false;
  subscription;
  paramDirection: Direction;
  showContent = false;
  shortcuts = [
    ShortcutAction.ONE,
    ShortcutAction.TWO,
    ShortcutAction.THREE,
    ShortcutAction.FOUR
  ];

  constructor(public api: ApiService,
    public cart: CartService,
    public request: RequestService,
    public nav: NavigateService,
    private inactivity: InactivityService,
    private route: ActivatedRoute,
    private scanDoc : ScanDocumentService,
    private place: PlacesService,
    private loader: LoaderService,
    public lang: LangService) {
    this.route.queryParams.subscribe(params => {
      if(params && params.direction) this.paramDirection = params.direction;
    });
    const webKiosk = sessionStorage.getItem('webKiosk') === 'true';
    this.subscription = this.cart.currentApplication$.subscribe(() => {
      if(!this.cart.currentRequest) {
        if(this.cart.currentApplication?.isRevision || (this.api.userRole.isWorker && this.api.hasPerm(UserPermission.ALLOW_CREATE) && this.api.hasPerm(UserPermission.ALLOW_CONSUME))) {
          this.nav.to('dashboard');
        } else if(this.api.userRole.isKiosk && !webKiosk) {
          this.nav.to('standby-kiosk');
        } else if(this.api.env.formHasTravelNameStep) {
          this.nav.to('travel-name');
        } else if(this.api.env.hasMultiPersonView) {
          this.nav.to('participants');
        }
      } else if(this.api.env.formHasPassportTypeStep && (!this.cart.currentRequest.nationality || !this.cart.currentRequest.passportType)) {
        this.nav.to('passport-type');
        return;
      }
      this.cart.currentRequest = {...this.cart.currentRequest, latestRouteVisited: 'visa-type'};
      this.cart.saveCurrentApplication(false);
      this.getVisaTypes();
      this.selected = this.cart.currentRequest?.visaType || undefined;
    });
    if(!this.inactivity.isWatching && this.api.userRole.isKiosk) this.inactivity.startWatching();
  }

  ngOnDestroy() {
    if(!this.keepScanned) {
      this.scanDoc.scanned = false;
    }
    this.subscription.unsubscribe();
  }

  getVisaTypes() {
    const [needsAutoSelect, visaTypes] = getVisaTypes(this.api.userInfo.server.paymentRequests, this.api, this.cart, this.place, this.scanDoc.consumeMode);
    this.visaTypes = visaTypes;

    if(needsAutoSelect) {
      this.autoSelect();
      return;
    }

    // Check if selected visa type is still valid
    if(this.selected && !this.visaTypes.some(v => v.id === this.selected.id)) {
      this.selectVisaType(undefined);
    }

    // Handle revisions
    if(this.cart.currentApplication.isRevision && this.cart.currentApplication.skipValidRevision && this.cart.isFnInRevision(VisaFN.VISA_TYPE, true)) {
      this.paramDirection === Direction.NEXT ? this.next() : this.previous();
      return;
    }

    this.autoSelect();
  }

  private autoSelect() {
    if(this.selected && !this.visaTypes.some(v => v.id === this.selected.id)) this.selected = undefined;

    // Auto-redirect if only one visa type available
    if(this.visaTypes.length === 1) {
      if(!this.selected) {
        this.selected = this.visaTypes[0];
        this.cart.currentRequest = {
          ...this.cart.currentRequest,
          visaType: this.visaTypes[0],
          formConfig: undefined
        };
        this.cart.saveCurrentApplication();
      }

      this.showContent = false;

      // Redirect based on direction parameter
      if(this.paramDirection === Direction.PREVIOUS) {
        this.previous();
      } else {
        this.next();
      }
      return;
    }

    // Auto-select first option for workers/admins
    if(!this.selected && (this.api.userRole.isWorker || this.api.userRole.isAdmin)) {
      this.selectVisaType(this.visaTypes[0], false);
    }

    // Show content with delay to prevent blinking
    setTimeout(() => {
      this.showContent = this.visaTypes && this.visaTypes.length > 0 && (this.visaTypes.length > 1 || this.selected !== undefined);
    }, 50);
  }

  focusSelect(visaType: Document) {
    if(this.cart.isFnInRevision(VisaFN.VISA_TYPE, true)) return;
    if(this.selected?.id === visaType.id) return;
    this._selectVisaTypeContinue(visaType, false);
  }

  selectVisaType(visaType: Document, next = true) {
    if(this.cart.isFnInRevision(VisaFN.VISA_TYPE, true)) return;
    if(documentTypeIsVisaMultiple(visaType?.id)) {
      this.loader.loading(true, {
        type: 'warn',
        btnLabel: 'global.confirm',
        custom: {
          innerHtml: this.lang.transform('multipleEntry.warning')
        }
      }).then((confirmed) => {
        if(confirmed) {
          this._selectVisaTypeContinue(visaType, next);
        }
      });
    } else {
      this._selectVisaTypeContinue(visaType, next);
    }
  }

  private _selectVisaTypeContinue(visaType: Document, next = true) {
    this.selected = visaType;
    this.cart.currentRequest = {
      ...this.cart.currentRequest,
      visaType: visaType,
      formConfig: undefined
    };
    this.cart.saveCurrentApplication();
    if(!this.selected || !this.request.sometimesPaidOnline(this.selected?.id)) {
      this.cart.currentRequest.urgent = undefined;
    }
    if(next) this.next();
  }

  next() {
    if(this.api.env.formHasUrgentStep && this.request.sometimesPaidOnline(this.selected.id) && !this.api.userRole.isWorker && !this.api.userRole.isAdmin) {
      this.nav.to('urgent', undefined, {queryParams: {direction: Direction.NEXT}});
    } else if(this.api.env.formReasonRequestStep) {
      this.nav.to('reason-request', undefined, {queryParams: {direction: Direction.NEXT}});
    } else {
      if(this.scanDoc?.scanned) {
        this.keepScanned = true;
      }
      this.nav.to('form-visa', undefined, {queryParams: {direction: Direction.NEXT}});
    }
  }

  previous() {
    if(this.scanDoc?.scanned) {
      this.keepScanned = true;
    }

    if(this.api.env.formHasPassportTypeStep) {
      this.nav.to('passport-type', undefined, {queryParams: {direction: Direction.PREVIOUS}});
    } else if(this.api.env.hasMultiPersonView) {
      this.nav.to('participants', undefined, {queryParams: {direction: Direction.PREVIOUS}});
    } else {
      this.nav.to('dashboard');
    }
  }

  toBeSupervised(): boolean {
    return (this.api.userRole.isAdmin || this.api.userRole.isWorker)
    && !isCanHaveOnArrival({countryCat: setCurrentCountryCat({indexed: {Nationality: this.cart.currentRequest.nationality}})})
    && (this.cart.currentRequest.passportType === PassportType.ORDINARY || this.cart.currentRequest.passportType === PassportType.REFUGEE)
    && !this.cart.currentApplication.isLandingVisa;
  }

  protected readonly ShortcutAction = ShortcutAction;
}
